import React, { useRef, useState, useEffect } from 'react';
import { logMyEvent } from '../firebase';
import { 
  Monitor, Smartphone, Music, Image as ImageIcon, 
  User, ChevronLeft, Download, RotateCcw, 
  CheckCircle2, Loader2, Settings2
} from 'lucide-react';

import { useAudioEngine } from '../hooks/useAudioEngine';
import { VisualLibrary, drawAvatar, drawBackground } from '../utils/VisualLibrary';
import AudioPlayer from '../utils/AudioPlayer'; 
import { templates } from '../utils/templates';

const AudioToVideo = ({ isExporting, setIsExporting, exportProgress, setExportProgress }) => {
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const bgImageRef = useRef(null);
  const avatarImageRef = useRef(null);
  const animationRef = useRef(null);
  const visTypeRef = useRef('bars');

  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [bgImage, setBgImage] = useState(null);
  const [avatarImage, setAvatarImage] = useState(null);
  const [visType, setVisType] = useState('bars');
  const [isLoading, setIsLoading] = useState(false);
  const [aspectRatio, setAspectRatio] = useState('landscape');
  const [showExportMenu, setShowExportMenu] = useState(false);

  const { init, exportVideo } = useAudioEngine(audioRef, canvasRef);
  const dimensions = aspectRatio === 'landscape' ? { w: 800, h: 450 } : { w: 450, h: 800 };

  useEffect(() => { visTypeRef.current = visType; }, [visType]);

  useEffect(() => {
    logMyEvent('step_navigation', { step_number: step, step_name: `Step ${step}` });
  }, [step]);

  const handleStartVisualization = () => {
    const { analyzer } = init();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dataArray = new Uint8Array(analyzer.frequencyBinCount);

    const renderFrame = () => {
      animationRef.current = requestAnimationFrame(renderFrame);
      analyzer.getByteFrequencyData(dataArray);
      drawBackground(ctx, canvas, bgImageRef.current);
      if (VisualLibrary[visTypeRef.current]) VisualLibrary[visTypeRef.current](ctx, dataArray, canvas);
      if (avatarImageRef.current) drawAvatar(ctx, canvas, dataArray, avatarImageRef.current, aspectRatio);
    };

    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    renderFrame();
  };

  const handleAudioUpload = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setIsLoading(true);
      setTimeout(() => {
        setFile(URL.createObjectURL(selectedFile));
        setIsLoading(false);
        setStep(3);
      }, 1500);
    }
  };

  const nextStep = () => !isExporting && setStep(s => s + 1);
  const prevStep = () => !isExporting && setStep(s => s - 1);

  return (
    <div className="animate-in fade-in">

      {/* ── Step 1 – Aspect ratio ── */}
      {step === 1 && (
        <div className="text-center space-y-12 animate-in fade-in">
          <h2 className="text-4xl font-black">Bring your audio to life with visuals</h2>
          <p className="text-slate-400 leading-relaxed">
            Choose your aspect ratio to continue...
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto pt-4">
            <button
              onClick={() => { setAspectRatio('portrait'); nextStep(); }}
              className="group p-10 bg-slate-900 border border-slate-800 rounded-[32px] hover:border-indigo-500 transition-all text-center"
            >
              <Smartphone className="mx-auto mb-6 text-indigo-500 group-hover:scale-110 transition-transform" size={48} />
              <h3 className="text-xl font-bold">9:16 Portrait</h3>
              <p className="text-slate-400 leading-relaxed">Facebook · TikTok · Instagram</p>
            </button>
            <button
              onClick={() => { setAspectRatio('landscape'); nextStep(); }}
              className="group p-10 bg-slate-900 border border-slate-800 rounded-[32px] hover:border-indigo-500 transition-all text-center"
            >
              <Monitor className="mx-auto mb-6 text-indigo-500 group-hover:scale-110 transition-transform" size={48} />
              <h3 className="text-xl font-bold">16:9 Landscape</h3>
              <p className="text-slate-400 leading-relaxed">YouTube · Twitch</p>
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2 – Upload audio ── */}
      {step === 2 && (
        <div className="max-w-lg mx-auto text-center space-y-8 animate-in fade-in">
          <h2 className="text-3xl font-black uppercase tracking-tight">Upload Your Audio</h2>
          <div className="p-16 border-2 border-dashed border-slate-800 bg-slate-900/30 rounded-[40px] hover:border-indigo-500 transition-colors cursor-pointer relative flex flex-col items-center justify-center min-h-[300px]">
            {isLoading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="text-indigo-500 animate-spin mb-4" size={48} />
                <p className="text-indigo-400 font-bold animate-pulse">PROCESSING AUDIO...</p>
              </div>
            ) : (
              <>
                <Music className="mx-auto mb-6 text-indigo-500/20" size={64} />
                <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="audio/*" onChange={handleAudioUpload} />
                <p className="font-bold text-indigo-400 uppercase tracking-widest">Click to upload your sound</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Step 3 – Background ── */}
      {step === 3 && (
        <div className="space-y-8 animate-in fade-in">
          <h2 className="text-3xl font-black text-center uppercase tracking-tight">Select Background</h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {templates.map(t => (
              <div
                key={t.id}
                onClick={() => {
                  const img = new Image(); img.crossOrigin = "Anonymous"; img.src = t.url;
                  img.onload = () => { bgImageRef.current = img; setBgImage(t.url); nextStep(); };
                }}
                className="relative group cursor-pointer overflow-hidden rounded-2xl aspect-video border-2 border-transparent hover:border-indigo-500 transition-all"
              >
                <img src={t.url} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={t.name} />
                <div className="absolute inset-0 bg-indigo-600/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <CheckCircle2 className="text-white" />
                </div>
              </div>
            ))}
          </div>

          <div className="relative flex py-5 items-center">
            <div className="flex-grow border-t border-slate-800"></div>
            <span className="flex-shrink mx-4 text-slate-600 font-bold uppercase text-xs tracking-widest">Or Upload Your Own</span>
            <div className="flex-grow border-t border-slate-800"></div>
          </div>

          <div className="text-center">
            <input
              type="file"
              id="bg-upload"
              className="hidden"
              accept="image/*"
              onChange={(e) => {
                if (e.target.files[0]) {
                  const img = new Image();
                  img.src = URL.createObjectURL(e.target.files[0]);
                  img.onload = () => { bgImageRef.current = img; setBgImage(img.src); nextStep(); };
                }
              }}
            />
            <label
              htmlFor="bg-upload"
              className="flex flex-col items-center justify-center p-8 w-full bg-slate-900/50 border-2 border-dashed border-slate-800 rounded-3xl cursor-pointer hover:bg-slate-900 hover:border-indigo-500/50 transition-all group"
            >
              <ImageIcon className="mb-3 text-slate-500 group-hover:text-indigo-500 transition-colors" size={32} />
              <span className="font-bold text-slate-400 group-hover:text-slate-200">Click to upload a custom image</span>
              <span className="text-xs text-slate-600 mt-1 uppercase tracking-tighter">JPG, PNG or WebP</span>
            </label>
          </div>

          <div className="text-center pt-4">
            <button
              onClick={prevStep}
              className="text-slate-500 flex items-center gap-2 mx-auto uppercase text-xs font-bold tracking-widest hover:text-white transition-colors"
            >
              <ChevronLeft size={16} /> Go Back
            </button>
          </div>
        </div>
      )}

      {/* ── Step 4 – Avatar ── */}
      {step === 4 && (
        <div className="max-w-lg mx-auto text-center space-y-8 animate-in fade-in">
          <h2 className="text-3xl font-black uppercase tracking-tight">Set Your Avatar</h2>
          <div className="bg-slate-900 p-12 rounded-[40px] border border-slate-800 shadow-2xl">

            <div className="w-32 h-32 bg-slate-950 rounded-full mx-auto mb-8 border-4 border-indigo-500/20 overflow-hidden flex items-center justify-center">
              {avatarImage ? (
                <img src={avatarImage} className="w-full h-full object-cover" alt="Avatar" />
              ) : (
                <User className="text-slate-800" size={50} />
              )}
            </div>

            <input
              type="file"
              id="av-upload"
              className="hidden"
              accept="image/*"
              onChange={(e) => {
                const img = new Image();
                img.src = URL.createObjectURL(e.target.files[0]);
                img.onload = () => { avatarImageRef.current = img; setAvatarImage(img.src); nextStep(); };
              }}
            />

            <label
              htmlFor="av-upload"
              className="bg-indigo-600 text-white px-10 py-4 rounded-2xl font-black cursor-pointer inline-block hover:bg-indigo-500 transition-all active:scale-95 shadow-lg shadow-indigo-500/20"
            >
              CHOOSE AVATAR
            </label>

            <button
              onClick={nextStep}
              className="block mx-auto mt-8 text-slate-500 font-bold uppercase text-xs hover:text-indigo-400 transition-colors"
            >
              Skip this step
            </button>
          </div>
        </div>
      )}

      {/* ── Step 5 – Preview & export ── */}
      {step === 5 && (
        <div className="space-y-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">

            <div className="w-full md:w-auto">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Style</label>
              <div className="relative">
                <select
                  value={visType}
                  onChange={(e) => setVisType(e.target.value)}
                  className="w-full md:w-64 bg-slate-900 border border-slate-800 text-slate-50 py-3 px-4 rounded-xl appearance-none focus:outline-none focus:border-indigo-500 font-bold"
                >
                  {Object.keys(VisualLibrary).map(type => (
                    <option key={type} value={type}>{type.toUpperCase()}</option>
                  ))}
                </select>
                <Settings2 size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              </div>
            </div>

            <div className="flex gap-4 items-center">
              <button
                onClick={() => setStep(1)}
                className="p-4 bg-slate-900 rounded-2xl border border-slate-800 hover:text-indigo-400 hover:border-indigo-800 transition-colors"
              >
                <RotateCcw size={22} />
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black flex items-center gap-3 shadow-lg shadow-indigo-500/20 hover:bg-indigo-500 hover:scale-105 transition-all relative z-10"
                >
                  <Download size={22} /> EXPORT VIDEO
                </button>

                {showExportMenu && (
                  <div
                    className="absolute top-full mt-3 right-0 w-72 rounded-[2rem] overflow-hidden shadow-2xl z-50 border border-slate-800"
                    style={{ backgroundColor: '#020617' }}
                  >
                    <div className="p-2">
                      <button
                        onClick={() => { setShowExportMenu(false); exportVideo('webm', setIsExporting, handleStartVisualization); }}
                        className="w-full p-5 hover:bg-slate-800 rounded-[1.5rem] flex flex-col items-start transition-all text-left"
                      >
                        <span className="font-bold text-white">WebM Video</span>
                        <span className="text-[10px] text-indigo-400 font-bold uppercase">Fast Export</span>
                      </button>
                      <button
                        onClick={() => { setShowExportMenu(false); exportVideo('mp4', setIsExporting, handleStartVisualization, setExportProgress); }}
                        className="w-full p-5 hover:bg-slate-800 rounded-[1.5rem] flex flex-col items-start transition-all text-left mt-1"
                      >
                        <span className="font-bold text-white">MP4 Video (HD)</span>
                        <span className="text-[10px] text-slate-500 font-bold uppercase">Slower · High Quality</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-slate-900/50 p-10 rounded-[48px] border border-slate-800 shadow-2xl">
            <AudioPlayer file={file} audioRef={audioRef} onPlayAction={handleStartVisualization} />
            <div className="flex justify-center mt-10">
              <div className="relative rounded-3xl overflow-hidden border-8 border-slate-800 shadow-2xl">
                <canvas ref={canvasRef} width={dimensions.w} height={dimensions.h} className="max-w-full h-auto bg-black"></canvas>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AudioToVideo;
